import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown,
  ChevronUp,
  Download,
  Eye,
  Pencil,
  Plus,
  Power,
  Printer,
  RotateCcw,
} from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { usePageActions } from "@/shared/hooks/usePageActions";
import { useToast } from "@/shared/providers/ToastProvider";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { StatusBadge } from "../components/StatusBadge";
import { inputClass, selectClass } from "../components/FormField";
import { providerTypeOptions } from "../data";
import { useAdminProviders } from "../hooks/useAdminProviders";
import type { Provider } from "../types";

export function ProvidersPage() {
  const { t } = useTranslation();
  const { exportTable, printPage } = usePageActions();
  const { showToast } = useToast();
  const { providers, loading, error, usingApi, provinceOptions, toggleStatus } = useAdminProviders();
  const [globalSearch, setGlobalSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [cityFilter, setCityFilter] = useState("");
  const [provinceFilter, setProvinceFilter] = useState("");
  const [tableSearch, setTableSearch] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const typeOptions = useMemo(
    () => [
      ...providerTypeOptions,
      { value: "LABORATOIRE", label: "Laboratoire" },
    ],
    [],
  );

  const filtered = useMemo(() => {
    return providers.filter((provider) => {
      const byGlobalSearch = globalSearch
        ? provider.commercialName.toLowerCase().includes(globalSearch.toLowerCase())
        : true;
      const byType = typeFilter ? provider.type === typeFilter : true;
      const byStatus = statusFilter ? provider.status === statusFilter : true;
      const byCity = cityFilter
        ? provider.city.toLowerCase().includes(cityFilter.toLowerCase())
        : true;
      const byProvince = provinceFilter ? provider.province === provinceFilter : true;
      const byTable = tableSearch
        ? `${provider.id} ${provider.commercialName} ${provider.city} ${provider.phone} ${provider.email}`
            .toLowerCase()
            .includes(tableSearch.toLowerCase())
        : true;

      return byGlobalSearch && byType && byStatus && byCity && byProvince && byTable;
    });
  }, [providers, globalSearch, typeFilter, statusFilter, cityFilter, provinceFilter, tableSearch]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * pageSize;
  const pageData = filtered.slice(start, start + pageSize);

  const handleExport = () => {
    exportTable(
      "prestataires.csv",
      ["ID", "Nom Commercial", "Type", "Ville", "Téléphone", "Email", "Statut"],
      filtered.map((provider) => [
        provider.id,
        provider.commercialName,
        provider.type,
        provider.city,
        provider.phone,
        provider.email,
        provider.status === "active" ? "ACTIF" : "INACTIF",
      ]),
    );
  };

  const getTypeVariant = (type: Provider["type"]) => {
    if (type === "PHARMACIE") return "success";
    if (type === "CABINET MÉDICAL") return "warning";
    if (type === "CLINIQUE") return "info";
    if (type === "LABORATOIRE") return "neutral";
    return "purple";
  };

  const handleToggle = async (provider: Provider) => {
    if (!usingApi) return;
    const label = provider.status === "active" ? "désactiver" : "activer";
    if (!window.confirm(`Voulez-vous vraiment ${label} « ${provider.commercialName} » ?`)) return;

    setTogglingId(provider.id);
    try {
      await toggleStatus(provider);
      showToast("Statut mis à jour.");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Mise à jour impossible.");
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t.safecheck.nav.management },
          { label: t.safecheck.providers.title },
        ]}
      />

      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-foreground">{t.safecheck.providers.listTitle}</h1>
          {usingApi && !loading ? (
            <p className="mt-1 text-sm text-muted">{filtered.length} prestataire(s)</p>
          ) : null}
          {loading ? <p className="mt-1 text-sm text-muted">Chargement…</p> : null}
          {error ? <p className="mt-1 text-sm text-red-600">{error}</p> : null}
        </div>
        <Link
          to="/dashboard/providers/create"
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
        >
          <Plus size={16} />
          {t.safecheck.providers.add}
        </Link>
      </div>

      <div className="theme-card p-5">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
          <input
            value={globalSearch}
            onChange={(event) => {
              setGlobalSearch(event.target.value);
              setPage(1);
            }}
            placeholder="Rechercher..."
            className={inputClass}
          />
          <select
            value={typeFilter}
            onChange={(event) => {
              setTypeFilter(event.target.value);
              setPage(1);
            }}
            className={selectClass}
          >
            {typeOptions.map((option) => (
              <option key={option.value || "all"} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setPage(1);
            }}
            className={selectClass}
          >
            <option value="">Tous les statuts</option>
            <option value="active">Actif</option>
            <option value="inactive">Inactif</option>
          </select>
          <input
            value={cityFilter}
            onChange={(event) => {
              setCityFilter(event.target.value);
              setPage(1);
            }}
            placeholder="Ville"
            className={inputClass}
          />
          <select
            value={provinceFilter}
            onChange={(event) => {
              setProvinceFilter(event.target.value);
              setPage(1);
            }}
            className={selectClass}
          >
            {provinceOptions.map((option) => (
              <option key={option.value || "all-province"} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleExport}
            className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-card-muted"
          >
            <Download size={14} />
            Exporter CSV
          </button>
          <button
            type="button"
            onClick={printPage}
            className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-card-muted"
          >
            <Printer size={14} />
            Imprimer
          </button>
          <button
            type="button"
            onClick={() => {
              setGlobalSearch("");
              setTypeFilter("");
              setStatusFilter("active");
              setCityFilter("");
              setProvinceFilter("");
              setTableSearch("");
              setPage(1);
            }}
            className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-card-muted"
          >
            <RotateCcw size={14} />
            Réinitialiser
          </button>
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-y border-border py-3">
          <div className="flex items-center gap-2 text-sm text-muted">
            <span>{t.safecheck.show}</span>
            <select
              value={pageSize}
              onChange={(event) => {
                setPageSize(Number(event.target.value));
                setPage(1);
              }}
              className={selectClass}
            >
              {[10, 25, 50].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span>{t.safecheck.entries}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted">{t.safecheck.searchLabel}</span>
            <input
              value={tableSearch}
              onChange={(event) => {
                setTableSearch(event.target.value);
                setPage(1);
              }}
              className="h-8 rounded-md border border-border bg-card px-3 text-sm outline-none focus:border-primary"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px]">
            <thead>
              <tr className="border-b border-border bg-card-muted/50">
                {["ID", "Nom Commercial", "Type", "Ville", "Contact", "Statut", "Actions"].map((title) => (
                  <th
                    key={title}
                    className="px-4 py-3 text-left text-[11px] font-semibold tracking-wide text-muted"
                  >
                    <span className="inline-flex items-center gap-1 uppercase">
                      {title}
                      <span className="flex flex-col">
                        <ChevronUp size={10} className="-mb-1" />
                        <ChevronDown size={10} />
                      </span>
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageData.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted">
                    —
                  </td>
                </tr>
              ) : (
                pageData.map((provider) => (
                  <tr key={provider.id} className="border-b border-border last:border-0 hover:bg-card-muted/30">
                    <td className="px-4 py-3 text-sm text-foreground">{provider.id}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-foreground">{provider.commercialName}</td>
                    <td className="px-4 py-3">
                      <StatusBadge variant={getTypeVariant(provider.type)}>
                        {provider.type}
                      </StatusBadge>
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">{provider.city}</td>
                    <td className="px-4 py-3 text-xs text-foreground">
                      <p>{provider.phone}</p>
                      <p className="text-muted">{provider.email}</p>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge variant={provider.status === "active" ? "success" : "neutral"}>
                        {provider.status === "active" ? "ACTIF" : "INACTIF"}
                      </StatusBadge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Link
                          to={`/dashboard/providers/${provider.id}`}
                          className="inline-flex h-7 w-7 items-center justify-center rounded border border-teal/50 text-teal hover:bg-teal-soft"
                          title="Voir"
                        >
                          <Eye size={13} />
                        </Link>
                        <Link
                          to={`/dashboard/providers/${provider.id}/edit`}
                          className="inline-flex h-7 w-7 items-center justify-center rounded border border-primary/50 text-primary hover:bg-primary-soft"
                          title="Modifier"
                        >
                          <Pencil size={13} />
                        </Link>
                        {usingApi ? (
                          <button
                            type="button"
                            disabled={togglingId === provider.id}
                            onClick={() => void handleToggle(provider)}
                            className="inline-flex h-7 w-7 items-center justify-center rounded border border-yellow/50 text-yellow hover:bg-yellow-soft disabled:opacity-50"
                            title={provider.status === "active" ? "Désactiver" : "Activer"}
                          >
                            <Power size={13} />
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between border-t border-border px-5 py-4 text-sm text-muted">
          <span>
            Affichage de {filtered.length === 0 ? 0 : start + 1} à {Math.min(start + pageSize, filtered.length)} sur {filtered.length} entrées
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={currentPage === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-lg border border-border px-3 py-1.5 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Précédente
            </button>
            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="rounded-lg border border-border px-3 py-1.5 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Suivante
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
